import React from 'react';
import PropTypes from 'prop-types';
import { LETTER_URL } from '../../constants';
import NeedHelp from '../NeedHelp';

const ConfirmationApproved = ({ confirmationDate, printPage, formData }) => {
  // Helper function to render a field if it exists
  const renderField = (label, value) => {
    if (!value || (typeof value === 'string' && !value.trim())) {
      return null;
    }
    return (
      <div className="vads-u-margin-bottom--2">
        <h4 className="vads-u-margin-bottom--0p5 vads-u-font-weight--bold">
          {label}
        </h4>
        <p className="vads-u-margin--0">{value}</p>
      </div>
    );
  };

  // Helper to format full name
  const formatFullName = nameObj => {
    if (!nameObj) return '';
    const parts = [
      nameObj.first,
      nameObj.middle,
      nameObj.last,
      nameObj.suffix,
    ].filter(Boolean);
    return parts.join(' ');
  };

  // Helper to format address
  const formatAddress = addressObj => {
    if (!addressObj) return '';
    const parts = [
      addressObj.street,
      addressObj.street2,
      addressObj.city,
      addressObj.state,
      addressObj.postalCode,
      addressObj.country,
    ].filter(Boolean);
    return parts.join(', ');
  };

  // Helper to format date
  const formatDate = dateString => {
    if (!dateString) return '';
    const date = new Date(`${dateString}T00:00:00`);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  // Helper to format yes/no
  const formatYesNo = value => {
    if (value === true) return 'Yes';
    if (value === false) return 'No';
    return '';
  };

  // Helper to format phone number
  const formatPhone = phoneObj => {
    if (!phoneObj) return '';
    if (typeof phoneObj === 'string') return phoneObj;
    return phoneObj.contact || phoneObj.phone || '';
  };

  // Helper to format salary from camelCase
  const formatSalary = salaryValue => {
    if (!salaryValue) return '';
    // Convert camelCase to readable format
    return salaryValue
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, str => str.toUpperCase())
      .trim();
  };

  return (
    <div className="vt2-confirmation-page vt2-confirmation-page_approved">
      <va-alert status="success">
        <h2 slot="headline">
          Congratulations! You’ve been approved for the VET TEC 2.0 Program
        </h2>
        <p>
          We reviewed your application and have determined that you are eligible
          for educational benefits under the VET TEC 2.0 Program. Your
          Certificate of Eligibility is now available. A physical copy will also
          be mailed to your mailing address. Please be aware that VET TEC 2.0
          has an annual student participation limit (October 1 to September 30).
          You are eligible, but if the participation limit has been met, we may
          not be able to award you benefits in this fiscal year.
        </p>
        <va-link
          download
          href={LETTER_URL}
          filetype="PDF"
          text="Download your certificate of eligibility"
          class="vads-u-margin-top--2"
        />
      </va-alert>

      <div className="vads-u-margin-top--3">
        {confirmationDate && (
          <p>
            <strong>Date received:</strong> {confirmationDate}
          </p>
        )}

        <va-accordion open-single>
          <va-accordion-item header="Information you submitted on this form">
            {/* Applicant Information */}
            {formData?.applicantFullName &&
              renderField(
                'Full name',
                formatFullName(formData.applicantFullName),
              )}
            {renderField(
              'Date of birth',
              formData?.dateOfBirth && formatDate(formData.dateOfBirth),
            )}
            {renderField('Social Security number', formData?.claimantSsn)}

            {/* Contact Information */}
            {renderField('Email address', formData?.contactInfo?.emailAddress)}
            {renderField(
              'Mobile phone',
              formatPhone(formData?.contactInfo?.mobilePhone),
            )}
            {renderField(
              'Home phone',
              formatPhone(formData?.contactInfo?.homePhone),
            )}
            {renderField('Contact method', formData?.contactMethod)}

            {/* Address Information */}
            {formData?.mailingAddress &&
              renderField(
                'Mailing address',
                formatAddress(formData.mailingAddress),
              )}

            {/* Military Information */}
            {formData?.dutyRequirementMet !== undefined &&
              renderField(
                'Met duty requirement',
                formatYesNo(formData.dutyRequirementMet),
              )}
            {renderField(
              'Date released from active duty',
              formData?.dateReleasedFromActiveDuty &&
                formatDate(formData.dateReleasedFromActiveDuty),
            )}
            {formData?.activeDutyDuringHitechVets !== undefined &&
              renderField(
                'On active duty during HI-TECH VETS',
                formatYesNo(formData.activeDutyDuringHitechVets),
              )}

            {/* Employment Information */}
            {formData?.isEmployed !== undefined &&
              renderField(
                'Currently employed',
                formatYesNo(formData.isEmployed),
              )}
            {formData?.isInTechnologyIndustry !== undefined &&
              renderField(
                'Working in technology industry',
                formatYesNo(formData.isInTechnologyIndustry),
              )}
            {renderField(
              'Technology area of focus',
              formData?.technologyAreaOfFocus,
            )}
            {renderField(
              'Current salary',
              formatSalary(formData?.currentSalary),
            )}
            {renderField(
              'Highest level of education',
              formData?.highestLevelOfEducation,
            )}

            {/* Training Providers */}
            {formData?.trainingProviders &&
              formData.trainingProviders.length > 0 && (
                <div className="vads-u-margin-bottom--2">
                  <h4 className="vads-u-margin-bottom--0p5 vads-u-font-weight--bold">
                    Training providers
                  </h4>
                  {formData.trainingProviders.map((provider, index) => (
                    <div key={index} className="vads-u-margin-bottom--1">
                      <p className="vads-u-margin--0">
                        <strong>{provider.providerName}</strong>
                      </p>
                      {provider.providerAddress && (
                        <p className="vads-u-margin--0">
                          {formatAddress(provider.providerAddress)}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            {renderField(
              'Planned training start date',
              formData?.plannedStartDate &&
                formatDate(formData.plannedStartDate),
            )}

            {/* Direct Deposit Information */}
            {formData?.bankAccount && (
              <>
                {renderField('Account type', formData.bankAccount.accountType)}
                {renderField(
                  'Routing number',
                  formData.bankAccount.routingNumber,
                )}
                {renderField(
                  'Account number',
                  formData.bankAccount.accountNumber &&
                    `****${formData.bankAccount.accountNumber.slice(-4)}`,
                )}
              </>
            )}
          </va-accordion-item>
        </va-accordion>

        <h3>Print this confirmation page</h3>
        <p>
          If you’d like to keep a copy of the information on this page, you can
          print it now. You won’t be able to access this page later. Please note
          that an email with this information will be automatically sent to your
          email provided on the application.
        </p>
        <va-button
          text="Print this page for your records"
          onClick={printPage}
        />
      </div>

      <h2>What to expect next</h2>
      <va-process-list>
        <va-process-list-item header="Download your Certificate of Eligibility">
          <p>
            Once you’ve confirmed a school or program, you may bring your
            Certificate of Eligibility to your School Certifying Official to
            provide proof of eligibility. Please be aware that VET TEC 2.0 has
            an annual student participation limit (October 1 to September 30).
            You are eligible, but if the participation limit has been met, we
            may not be able to award you benefits in this fiscal year.
          </p>
        </va-process-list-item>
        <va-process-list-item header="We’ll check the training provider(s) you listed">
          <p>
            If you provided a school, we’ll review whether that school has
            programs currently approved for the VET TEC 2.0 Program. If it
            doesn’t, we’ll reach out to the school to explore if approval can be
            set up.
          </p>
          <p>
            <va-link
              href="/education/gi-bill-comparison-tool/"
              text="Use the GI Bill Comparison Tool"
            />{' '}
            to find the best education program for you.
          </p>
        </va-process-list-item>
        <va-process-list-item header="We’ll keep you updated">
          <ul>
            <li>
              Review and/or update your direct deposit information on your{' '}
              <va-link href="/profile" text="VA.gov profile" />.
            </li>
            <li>
              Learn more about VA benefits and programs through the{' '}
              <va-link
                href="https://www.va.gov/education/"
                text="VA Education and Training Benefits page"
                external
              />
              .
            </li>
          </ul>
        </va-process-list-item>
      </va-process-list>

      <va-additional-info trigger="What is a Certificate of Eligibility?">
        <p>
          A Certificate of Eligibility is an official document from the U.S.
          Department of Veterans Affairs that details your GI Bill benefit
          status. You may provide this official document to your educational
          institution to prove your eligibility status.
        </p>
        <va-link
          href="https://benefits.va.gov/gibill/understandingyourcoe.asp"
          external
          text="Understanding your Certificate of Eligibility"
        />
      </va-additional-info>

      <h2>How to contact us if you have questions</h2>
      <p>
        If you have questions about this form or need help, you can submit a
        request with{' '}
        <va-link href="https://www.va.gov/contact-us" external text="Ask VA" />.
      </p>

      <va-link-action
        href="/"
        text="Go back to VA.gov homepage"
        type="primary"
        class="vads-u-margin-top--2"
      />

      <div className="help-footer-box vads-u-margin-top--4">
        <h2 className="help-heading">Need help?</h2>
        <NeedHelp />
      </div>
    </div>
  );
};

ConfirmationApproved.propTypes = {
  confirmationDate: PropTypes.string,
  formData: PropTypes.object,
  printPage: PropTypes.func,
};

export default ConfirmationApproved;
